import React, { useState, useEffect } from 'react';
// ... existing code ...
import payrollIntegrationService from '../../../services/payrollIntegrationService';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';




const PayrollIntegrationWizard = ({ onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    providerName: '',
    payPeriodStart: '',
    payPeriodEnd: '',
    integrationMethod: 'csv_import',
    file: null,
    fileName: ''
  });
  const [providerConfigs, setProviderConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [integrationSession, setIntegrationSession] = useState(null);
  const [validationResults, setValidationResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const steps = [
    { number: 1, title: 'Select Payroll Provider', icon: 'Building' },
    { number: 2, title: 'Select Pay Period', icon: 'Calendar' },
    { number: 3, title: 'Upload Payroll Data', icon: 'Upload' },
    { number: 4, title: 'Validate & Import', icon: 'CheckCircle' }
  ];

  const payrollProviders = [
    { value: 'able', label: 'ABLE Payroll', description: 'PNG payroll system with comprehensive features', icon: 'FileText' },
    { value: 'smoothpay', label: 'Smoothpay', description: 'Modern cloud-based payroll solution', icon: 'Cloud' },
    { value: 'bsp_quick_pay', label: 'BSP Quick Pay', description: 'Bank South Pacific payroll service', icon: 'CreditCard' },
    { value: 'alesco', label: 'ALESCO', description: 'Enterprise HR and payroll platform', icon: 'Briefcase' }
  ];

  useEffect(() => {
    loadProviderConfigs();
  }, []);

  const loadProviderConfigs = async () => {
    try {
      const configs = await payrollIntegrationService?.getAllProviderConfigs();
      setProviderConfigs(configs);
    } catch (err) {
      console.error('Failed to load provider configs:', err);
    }
  };

  const handleProviderSelect = (providerName) => {
    setFormData({ ...formData, providerName });
    const config = providerConfigs?.find(c => c?.providerName === providerName && c?.isActive);
    setSelectedConfig(config);
    setError('');
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        file: file,
        fileName: file?.name
      }));
      setError('');
    }
  };

  const handleStep1Next = () => {
    if (!formData?.providerName) {
      setError('Please select a payroll provider');
      return;
    }
    if (!selectedConfig) {
      setError('Selected provider is not configured. Please contact administrator.');
      return;
    }
    setError('');
    setCurrentStep(2);
  };

  const handleStep2Next = () => {
    if (!formData?.payPeriodStart || !formData?.payPeriodEnd) {
      setError('Please select pay period dates');
      return;
    }
    setError('');
    setCurrentStep(3);
  };

  const handleStep3Next = async () => {
    if (!formData?.file) {
      setError('Please upload payroll data file');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Import from CSV/Excel
      const result = await payrollIntegrationService?.importFromCSV(
        formData?.file,
        selectedConfig?.id,
        formData?.payPeriodStart,
        formData?.payPeriodEnd,
        'system'
      );

      setIntegrationSession(result);
      setValidationResults(result?.validationResults || []);
      setCurrentStep(4);
    } catch (err) {
      setError(err?.message || 'Failed to import payroll data');
    } finally {
      setLoading(false);
    }
  };

  const handleStep4Complete = () => {
    onComplete?.({
      integrationSession,
      validationResults
    });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select the payroll provider you want to integrate with. The app supports multiple PNG payroll systems.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {payrollProviders?.map((provider) => {
                const config = providerConfigs?.find(c => c?.providerName === provider?.value);
                const isActive = config?.isActive;
                const isSelected = formData?.providerName === provider?.value;
                
                return (
                  <button
                    key={provider?.value}
                    onClick={() => handleProviderSelect(provider?.value)}
                    disabled={!isActive}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : isActive
                        ? 'border-border hover:border-primary/50' :'border-border opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isSelected ? 'bg-primary text-white' : 'bg-muted text-foreground'
                      }`}>
                        <Icon name={provider?.icon} size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground mb-1">{provider?.label}</h4>
                        <p className="text-xs text-muted-foreground">{provider?.description}</p>
                        {!isActive && (
                          <p className="text-xs text-warning mt-2">Not configured</p>
                        )}
                        {isActive && config && (
                          <p className="text-xs text-success mt-2">
                            Method: {config?.integrationMethod?.replace('_', ' ')?.toUpperCase()}
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <Icon name="CheckCircle" size={20} className="text-primary" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select the pay period for the payroll data you want to import from {formData?.providerName?.toUpperCase()}.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Pay Period Start
                </label>
                <Input
                  type="date"
                  value={formData?.payPeriodStart}
                  onChange={(e) => setFormData({ ...formData, payPeriodStart: e?.target?.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Pay Period End
                </label>
                <Input
                  type="date"
                  value={formData?.payPeriodEnd}
                  onChange={(e) => setFormData({ ...formData, payPeriodEnd: e?.target?.value })}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload the payroll data file exported from {formData?.providerName?.toUpperCase()}. Supported formats: CSV, Excel.
            </p>
            {selectedConfig?.csvTemplateUrl && (
              <div className="bg-accent/10 border border-accent rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">Template Available</p>
                    <p className="text-xs text-muted-foreground mb-2">
                      Download the template to ensure correct data format
                    </p>
                    <a
                      href={selectedConfig?.csvTemplateUrl}
                      download
                      className="text-xs text-primary hover:underline"
                    >
                      Download CSV Template
                    </a>
                  </div>
                </div>
              </div>
            )}
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              {formData?.fileName ? (
                <div className="space-y-3">
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
                    <Icon name="FileCheck" size={32} className="text-success" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{formData?.fileName}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {(formData?.file?.size / 1024)?.toFixed(2)} KB
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData({ ...formData, file: null, fileName: '' })}
                  >
                    Remove File
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
                    <Icon name="Upload" size={32} className="text-muted-foreground" />
                  </div>
                  <div>
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="text-sm font-medium text-primary hover:underline">
                        Click to upload
                      </span>
                      <span className="text-sm text-muted-foreground"> or drag and drop</span>
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      CSV or Excel files up to 10MB
                    </p>
                  </div>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="bg-success/10 border border-success rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Import Completed</p>
                  <p className="text-xs text-muted-foreground">
                    Payroll data has been imported and validated against PNG labor laws
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Total Records</p>
                <p className="text-2xl font-bold text-foreground">{integrationSession?.totalRecords || 0}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Processed</p>
                <p className="text-2xl font-bold text-success">{integrationSession?.processedRecords || 0}</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Failed</p>
                <p className="text-2xl font-bold text-error">{integrationSession?.failedRecords || 0}</p>
              </div>
            </div>

            {validationResults?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">Validation Results</h4>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {validationResults?.map((result, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border ${
                        result?.status === 'passed' ?'bg-success/5 border-success/20'
                          : result?.status === 'warning' ?'bg-warning/5 border-warning/20' :'bg-error/5 border-error/20'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <Icon
                          name={result?.status === 'passed' ? 'CheckCircle' : result?.status === 'warning' ? 'AlertTriangle' : 'XCircle'}
                          size={16}
                          className={`flex-shrink-0 mt-0.5 ${
                            result?.status === 'passed' ? 'text-success' : result?.status === 'warning' ? 'text-warning' : 'text-error'
                          }`}
                        />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-foreground">{result?.employeeName}</p>
                          {result?.messages?.length > 0 && (
                            <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                              {result?.messages?.map((msg, msgIndex) => (
                                <li key={msgIndex}>• {msg}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Payroll Integration Wizard</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Import payroll data from PNG payroll providers with automatic validation
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-between">
          {steps?.map((step, index) => (
            <React.Fragment key={step?.number}>
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    currentStep === step?.number
                      ? 'bg-primary text-white'
                      : currentStep > step?.number
                      ? 'bg-success text-white' :'bg-muted text-muted-foreground'
                  }`}
                >
                  {currentStep > step?.number ? (
                    <Icon name="Check" size={20} />
                  ) : (
                    <Icon name={step?.icon} size={20} />
                  )}
                </div>
                <div className="hidden md:block">
                  <p className="text-xs text-muted-foreground">Step {step?.number}</p>
                  <p className="text-sm font-medium text-foreground">{step?.title}</p>
                </div>
              </div>
              {index < steps?.length - 1 && (
                <div className="flex-1 h-0.5 bg-border mx-4" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="p-6">
        {error && (
          <div className="mb-6 bg-error/10 border border-error rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-error font-medium">Error</p>
              <p className="text-sm text-error">{error}</p>
            </div>
          </div>
        )}

        {renderStepContent()}
      </div>
      {/* Footer */}
      <div className="p-6 border-t border-border flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => {
            if (currentStep === 1) {
              onCancel?.();
            } else {
              setCurrentStep(currentStep - 1);
              setError('');
            }
          }}
          disabled={loading}
        >
          {currentStep === 1 ? 'Cancel' : 'Back'}
        </Button>

        <Button
          variant="primary"
          onClick={() => {
            if (currentStep === 1) handleStep1Next();
            else if (currentStep === 2) handleStep2Next();
            else if (currentStep === 3) handleStep3Next();
            else if (currentStep === 4) handleStep4Complete();
          }}
          disabled={loading}
          iconName={loading ? 'Loader2' : currentStep === 4 ? 'Check' : 'ArrowRight'}
          iconPosition="right"
        >
          {loading ? 'Processing...' : currentStep === 4 ? 'Complete' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default PayrollIntegrationWizard;